/* A company's mark on a row.
 *
 * This was a monogram and nothing else, on the reasoning that a real logo meant
 * sending each company's domain to a third-party image service on every render,
 * would not work offline, and was a dependency the app deliberately did not
 * have. Two of those three were about *how* it would have been done rather than
 * about logos, and both go away when the fetch goes through the Worker once and
 * the answer is kept as bytes: the device never speaks to the logo service, the
 * mark is a `data:` URI that draws with no network and under the macOS shell's
 * `img-src 'self' data:`, and nothing was added to `package.json`. See
 * `/api/logo` in the Worker and `Logo` in types.ts.
 *
 * The monogram did not go anywhere. It is what a name without a logo gets, what
 * everything gets before the first fetch lands, and what the whole book gets
 * when the setting is off — so the row is never empty and never waiting. It
 * keeps the position's own colour either way, which is the same colour as its
 * donut slice and its theme bar. */

interface Props {
  ticker: string;
  color: string;
  size?: number;
  /** A `data:` URI, or nothing. */
  logo?: string | undefined;
}

/** Two letters where they read well, one where the ticker is short or the second
    character is not a letter. */
function monogram(ticker: string): string {
  const letters = ticker.replace(/[^A-Za-z]/g, '');
  if (letters.length <= 2) return letters.toUpperCase() || ticker.slice(0, 2).toUpperCase();
  return letters.slice(0, 2).toUpperCase();
}

export function TickerMark({ ticker, color, size = 34, logo }: Props) {
  return (
    <span
      className="ticker-mark"
      data-logo={logo ? 'true' : undefined}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        color,
        /* A logo brings its own colours and mostly its own white, so the tinted
           tile behind it would fight with it. The border stays: it is what
           keeps a row of mixed logos and monograms reading as one column. */
        background: logo ? 'var(--surface2)' : `color-mix(in srgb, ${color} 16%, transparent)`,
        borderColor: `color-mix(in srgb, ${color} 34%, transparent)`,
        fontSize: size * 0.36,
      }}
    >
      {logo
        ? <img src={logo} alt="" width={size} height={size} loading="lazy" decoding="async" />
        : monogram(ticker)}
    </span>
  );
}
